import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, Zap, Crown, AlertTriangle, CalendarClock, CreditCard } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import { usePlan, PlanPrice, PRO_BENEFITS, PRO_TAGLINE } from "@/lib/plan";
import { fmtDate } from "@/components/SubscriptionCard";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";


const Benefits = ({ dot = "bg-mint" }) => (
  <ul className="space-y-3" data-testid="billing-benefits">
    {PRO_BENEFITS.map((p) => (
      <li key={p} className="flex items-center gap-3">
        <span className={`w-5 h-5 rounded-md ${dot} border-2 border-ink flex items-center justify-center`}><Check className="w-3 h-3 stroke-[3]" /></span>
        <span className="text-sm font-medium">{p}</span>
      </li>
    ))}
  </ul>
);

export default function Billing() {
  const { refresh } = useAuth();
  const plan = usePlan();
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [info, setInfo] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadInfo = useCallback(async () => {
    try { const r = await api.get("/billing/me"); setInfo(r.data); }
    catch (e) { console.warn("billing info failed", e); }
  }, []);

  useEffect(() => { loadInfo(); }, [loadInfo]);

  const poll = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 8) { setPolling(false); toast.error("Still processing — refresh in a moment."); return; }
    try {
      const res = await api.get(`/billing/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setPolling(false);
        await refresh(); await loadInfo();
        toast.success("Welcome to GetFluentLoop Pro!");
        setParams({});
        return;
      }
      if (res.data.status === "expired") { setPolling(false); setParams({}); toast.error("Checkout expired."); return; }
      setTimeout(() => poll(sessionId, attempts + 1), 2000);
    } catch { setTimeout(() => poll(sessionId, attempts + 1), 2000); }
  }, [refresh, setParams, loadInfo]);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (sessionId) { setPolling(true); poll(sessionId); }
  }, [params, poll]);

  const go = async (path) => {
    setLoading(true);
    try {
      const res = await api.post(path, { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch { toast.error(path.endsWith("portal") ? "Could not open billing settings." : "Could not start checkout."); setLoading(false); }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      const res = await api.post("/billing/cancel");
      await refresh(); await loadInfo();
      toast.success(res.data.cancel_at_period_end
        ? `Your subscription ends ${fmtDate(res.data.access_until)} — Pro continues until then.`
        : "Subscription canceled.");
    } catch { toast.error("Couldn't cancel right now. Please try again."); }
    setBusy(false); setConfirmCancel(false);
  };

  const resume = async () => {
    setBusy(true);
    try { await api.post("/billing/resume"); await refresh(); await loadInfo(); toast.success("Your subscription will keep renewing."); }
    catch { toast.error("Couldn't resume right now. Please try again."); }
    setBusy(false);
  };

  const status = info?.subscription_status || "free";
  const isActive = status === "active";
  const isPastDue = status === "past_due";
  const willCancel = isActive && info?.cancel_at_period_end;
  const viaStripe = info?.managed_by_stripe;
  const entitled = info?.has_pro_access && info?.access_source !== "stripe";
  const stripeLive = viaStripe && (isActive || isPastDue);

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="font-cabinet font-extrabold text-3xl">Subscription</h1>
        {polling && <p className="text-coral font-bold mt-2" data-testid="billing-confirming">Confirming your subscription…</p>}

        {isPastDue && (
          <div className="nb-card bg-peach p-4 mt-5 flex items-start gap-3" data-testid="billing-pastdue-banner">
            <AlertTriangle className="w-6 h-6 shrink-0" />
            <div>
              <div className="font-cabinet font-bold">Your last payment didn't go through</div>
              <p className="text-sm text-gray-700">
                {info?.in_grace
                  ? `Pro stays on until ${fmtDate(info.grace_ends_at)} while we retry. Please update your payment details.`
                  : "Pro is paused until your payment details are updated. Your progress is safe."}
              </p>
            </div>
          </div>
        )}

        {entitled && (
          <div className="nb-card bg-mint p-6 mt-6" data-testid="billing-entitlement">
            <div className="nb-tag bg-white text-ink mb-2 flex items-center gap-1 w-fit"><Crown className="w-4 h-4" /> {info.is_owner ? "Owner" : info.is_lifetime ? "Lifetime" : "Education"}</div>
            <div className="font-cabinet font-extrabold text-3xl" data-testid="billing-entitlement-label">{info.is_owner ? "Lifetime Access · Owner" : info.is_lifetime ? "Lifetime Access" : "Education Access"}</div>
            <p className="text-sm text-gray-700 mt-2 mb-4">You have full access to every Pro feature — no subscription required.</p>
            <Benefits dot="bg-white" />
          </div>
        )}
        {(!entitled || stripeLive) && (
        <div className="nb-card p-0 overflow-hidden mt-6" data-testid="billing-subscription-section">
          <div className={`p-6 border-b-2 border-ink ${isActive ? "bg-mint" : isPastDue ? "bg-butter" : "bg-coral text-white"}`}>
            <div className="flex items-center justify-between">
              <div>
                {stripeLive && <div className="text-xs font-bold uppercase tracking-widest mb-2" data-testid="billing-your-subscription">Your subscription</div>}
                <div className="nb-tag bg-white text-ink mb-2 flex items-center gap-1 w-fit" data-testid="billing-plan-name"><Crown className="w-4 h-4" /> {plan?.name || "GetFluentLoop Pro"}</div>
                <PlanPrice plan={plan} className="font-cabinet font-extrabold text-4xl" />
                <p className="text-sm mt-1 opacity-90">{PRO_TAGLINE}</p>
              </div>
              <span className="nb-tag bg-white text-ink uppercase" data-testid="billing-status">{status.replace("_", " ")}</span>
            </div>
            {(isActive || isPastDue) && info?.current_period_end && (
              <div className="flex items-center gap-2 mt-4 text-sm font-bold" data-testid="billing-renewal">
                <CalendarClock className="w-4 h-4" />
                {willCancel ? `Cancels on ${fmtDate(info.current_period_end)}`
                  : isPastDue ? `Renewal was due ${fmtDate(info.current_period_end)}`
                  : viaStripe ? `Renews on ${fmtDate(info.current_period_end)}`
                  : `Access until ${fmtDate(info.current_period_end)}`}
              </div>
            )}
          </div>
          <div className="p-6 bg-white">
            <Benefits />
            {!isActive && !isPastDue && !entitled && (
              <button onClick={() => go("/billing/checkout")} disabled={loading || polling} className="nb-btn-primary w-full mt-6 py-3" data-testid="billing-upgrade-btn">
                <Zap className="w-5 h-5" /> {loading ? "Starting…" : "Subscribe to Pro"}
              </button>
            )}
            {isPastDue && viaStripe && (
              <button onClick={() => go("/billing/portal")} disabled={loading} className="nb-btn-primary w-full mt-6 py-3" data-testid="billing-update-payment-btn">
                <CreditCard className="w-5 h-5" /> {loading ? "Opening…" : "Update payment details"}
              </button>
            )}
            {isActive && viaStripe && (
              <button onClick={() => go("/billing/portal")} disabled={loading} className="nb-btn-outline w-full mt-6 py-3" data-testid="billing-manage-btn"><CreditCard className="w-5 h-5" /> {loading ? "Opening…" : "Manage payment details"}</button>
            )}
            {willCancel && viaStripe && (
              <button onClick={resume} disabled={busy} className="nb-btn-mint w-full mt-3 py-3" data-testid="billing-resume-btn">{busy ? "Resuming…" : "Resume subscription"}</button>
            )}
            {isActive && viaStripe && !willCancel && (
              <button onClick={() => setConfirmCancel(true)} className="w-full mt-3 text-sm text-gray-500 hover:text-coral py-2" data-testid="billing-cancel-btn">Cancel subscription</button>
            )}
          </div>
        </div>
        )}
        <AlertDialog open={confirmCancel} onOpenChange={(o) => !busy && setConfirmCancel(o)}>
          <AlertDialogContent data-testid="billing-cancel-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel GetFluentLoop Pro?</AlertDialogTitle>
              <AlertDialogDescription data-testid="billing-cancel-dialog-text">
                You'll keep Pro access until {fmtDate(info?.current_period_end)}. Your account, learning history and saved data will remain.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={busy} data-testid="billing-cancel-dialog-keep">Keep subscription</AlertDialogCancel>
              <AlertDialogAction disabled={busy} onClick={(e) => { e.preventDefault(); cancel(); }} data-testid="billing-cancel-dialog-confirm">{busy ? "Scheduling…" : "Cancel at period end"}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {(!entitled || stripeLive) && (
          <p className="text-xs text-gray-400 text-center mt-4" data-testid="billing-footnote">Secure payment via Stripe. Renews automatically every month until you cancel. Cancel anytime — Pro continues until the end of the paid period. Price includes applicable taxes.</p>
        )}
      </div>
    </AppLayout>
  );
}
